
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface TeenSubmissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const TeenSubmissionDialog = ({ open, onOpenChange, onSuccess }: TeenSubmissionDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    story: '',
    achievements: '',
    image_url: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.title.trim() || !formData.story.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in both title and story fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('teen_of_the_day_submissions')
        .insert({
          user_id: user.id,
          title: formData.title.trim(),
          story: formData.story.trim(),
          achievements: formData.achievements.trim() || null,
          image_url: formData.image_url.trim() || null
        });

      if (error) {
        throw error;
      }

      toast({
        title: "Submission Successful!",
        description: "Your request has been submitted for review. We'll notify you once it's reviewed by our team."
      });

      setFormData({ title: '', story: '', achievements: '', image_url: '' });
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error('Error submitting:', error);
      toast({
        title: "Submission Failed",
        description: error.message || "There was an error submitting your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Request to be Teen of the Day
          </DialogTitle>
          <DialogDescription>
            Share your inspiring story and achievements. Our team will review your submission and may feature you as Teen of the Day!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          <div className="space-y-2">
            <Label htmlFor="title" className="text-base font-medium">
              Title / What makes you special? *
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Young Entrepreneur, Environmental Activist, Teen Coder..."
              className="h-11"
              maxLength={100}
            />
            <p className="text-sm text-muted-foreground">
              {formData.title.length}/100 characters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="story" className="text-base font-medium">
              Your Story *
            </Label>
            <Textarea
              id="story"
              value={formData.story}
              onChange={(e) => handleInputChange('story', e.target.value)}
              placeholder="Tell us your inspiring story... What have you accomplished? What challenges have you overcome? What are you passionate about? How are you making a difference?"
              className="min-h-32 resize-y"
              maxLength={1000}
            />
            <p className="text-sm text-muted-foreground">
              {formData.story.length}/1000 characters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="achievements" className="text-base font-medium">
              Key Achievements (Optional)
            </Label>
            <Textarea
              id="achievements"
              value={formData.achievements}
              onChange={(e) => handleInputChange('achievements', e.target.value)}
              placeholder="List your key achievements, awards, projects, or milestones..."
              className="min-h-24 resize-y"
              maxLength={500}
            />
            <p className="text-sm text-muted-foreground">
              {formData.achievements.length}/500 characters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="image_url" className="text-base font-medium">
              Photo URL (Optional)
            </Label>
            <Input
              id="image_url"
              value={formData.image_url}
              onChange={(e) => handleInputChange('image_url', e.target.value)}
              placeholder="https://example.com/your-photo.jpg"
              type="url"
              className="h-11"
            />
            <p className="text-sm text-muted-foreground">
              Add a photo that represents you or your achievements
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.title.trim() || !formData.story.trim()}
              className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TeenSubmissionDialog;
